let youtubeApiPromise;

function loadYouTubeApi() {
  if (window.YT && window.YT.Player) {
    return Promise.resolve();
  }

  if (youtubeApiPromise) {
    return youtubeApiPromise;
  }

  youtubeApiPromise = new Promise(resolve => {
    const previousReady = window.onYouTubeIframeAPIReady;

    window.onYouTubeIframeAPIReady = () => {
      if (typeof previousReady === 'function') {
        previousReady();
      }
      resolve();
    };

    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    document.head.appendChild(script);
  });

  return youtubeApiPromise;
}

class FullWidthBannerVideo extends HTMLElement {
  constructor() {
    super();
    this.onPlayPauseClick = this.onPlayPauseClick.bind(this);
    this.onMuteClick = this.onMuteClick.bind(this);
    this.onProgressInput = this.onProgressInput.bind(this);
    this.onTimeUpdate = this.onTimeUpdate.bind(this);
    this.onLoadedMetadata = this.onLoadedMetadata.bind(this);
    this.onPlay = this.onPlay.bind(this);
    this.onPause = this.onPause.bind(this);
    this.onVideoClick = this.onVideoClick.bind(this);
    this.onYouTubeStateChange = this.onYouTubeStateChange.bind(this);
    this.isSeeking = false;
    this.isYouTube = false;
    this.ytPlayer = null;
    this.progressInterval = null;
  }

  connectedCallback() {
    this.videoType = this.dataset.videoType || 'html5';
    this.isYouTube = this.videoType === 'youtube';
    this.playPauseBtn = this.querySelector('[data-action="play-pause"]');
    this.muteBtn = this.querySelector('[data-action="mute"]');
    this.progress = this.querySelector('[data-progress]');
    this.currentTimeEl = this.querySelector('[data-time-current]');
    this.durationEl = this.querySelector('[data-time-duration]');

    this.playPauseBtn?.addEventListener('click', this.onPlayPauseClick);
    this.muteBtn?.addEventListener('click', this.onMuteClick);
    this.progress?.addEventListener('input', this.onProgressInput);
    this.progress?.addEventListener('change', () => {
      this.isSeeking = false;
    });
    this.progress?.addEventListener('mousedown', () => {
      this.isSeeking = true;
    });
    this.progress?.addEventListener('touchstart', () => {
      this.isSeeking = true;
    }, { passive: true });

    if (this.isYouTube) {
      this.initYouTube();
      return;
    }

    this.video = this.querySelector('video');
    if (!this.video) return;

    this.video.addEventListener('timeupdate', this.onTimeUpdate);
    this.video.addEventListener('loadedmetadata', this.onLoadedMetadata);
    this.video.addEventListener('durationchange', this.onLoadedMetadata);
    this.video.addEventListener('play', this.onPlay);
    this.video.addEventListener('pause', this.onPause);
    this.video.addEventListener('click', this.onVideoClick);

    this.updatePlayPauseState();
    this.updateMuteState();
    this.onLoadedMetadata();
    this.onTimeUpdate();
  }

  disconnectedCallback() {
    this.stopProgressPolling();
    this.playPauseBtn?.removeEventListener('click', this.onPlayPauseClick);
    this.muteBtn?.removeEventListener('click', this.onMuteClick);
    this.progress?.removeEventListener('input', this.onProgressInput);
    this.video?.removeEventListener('timeupdate', this.onTimeUpdate);
    this.video?.removeEventListener('loadedmetadata', this.onLoadedMetadata);
    this.video?.removeEventListener('durationchange', this.onLoadedMetadata);
    this.video?.removeEventListener('play', this.onPlay);
    this.video?.removeEventListener('pause', this.onPause);
    this.video?.removeEventListener('click', this.onVideoClick);

    if (this.ytPlayer?.destroy) {
      this.ytPlayer.destroy();
    }
  }

  initYouTube() {
    const videoId = this.dataset.videoId;
    const mount = this.querySelector('[data-youtube-mount]');

    if (!videoId || !mount) return;

    loadYouTubeApi().then(() => {
      if (!this.isConnected) return;

      this.ytPlayer = new window.YT.Player(mount, {
        videoId,
        width: '100%',
        height: '100%',
        playerVars: {
          autoplay: 1,
          controls: 0,
          disablekb: 1,
          fs: 0,
          iv_load_policy: 3,
          loop: 1,
          modestbranding: 1,
          mute: 1,
          playsinline: 1,
          rel: 0,
          playlist: videoId
        },
        events: {
          onReady: () => {
            this.updatePlayPauseState();
            this.updateMuteState();
            this.updateYouTubeProgress();
            this.startProgressPolling();
          },
          onStateChange: this.onYouTubeStateChange
        }
      });
    });
  }

  onYouTubeStateChange(event) {
    const state = event.data;
    const isPlaying = state === window.YT.PlayerState.PLAYING;
    const isPaused = state === window.YT.PlayerState.PAUSED;

    if (isPlaying || isPaused) {
      this.updatePlayPauseState();
    }

    if (isPlaying) {
      this.startProgressPolling();
    } else if (isPaused || state === window.YT.PlayerState.ENDED) {
      this.stopProgressPolling();
      this.updateYouTubeProgress();
    }
  }

  startProgressPolling() {
    this.stopProgressPolling();
    this.progressInterval = window.setInterval(() => {
      this.updateYouTubeProgress();
    }, 250);
  }

  stopProgressPolling() {
    if (this.progressInterval) {
      window.clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
  }

  formatTime(seconds) {
    if (!Number.isFinite(seconds) || seconds < 0) return '0:00';

    const totalSeconds = Math.floor(seconds);
    const minutes = Math.floor(totalSeconds / 60);
    const remainingSeconds = totalSeconds % 60;

    return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`;
  }

  isPlaying() {
    if (this.isYouTube) {
      if (!this.ytPlayer?.getPlayerState) return false;
      return this.ytPlayer.getPlayerState() === window.YT.PlayerState.PLAYING;
    }

    return this.video ? !this.video.paused : false;
  }

  isMuted() {
    if (this.isYouTube) {
      if (!this.ytPlayer?.isMuted) return true;
      return this.ytPlayer.isMuted();
    }

    return this.video ? this.video.muted : true;
  }

  getDuration() {
    if (this.isYouTube) {
      if (!this.ytPlayer?.getDuration) return 0;
      return this.ytPlayer.getDuration();
    }

    return this.video ? this.video.duration : 0;
  }

  getCurrentTime() {
    if (this.isYouTube) {
      if (!this.ytPlayer?.getCurrentTime) return 0;
      return this.ytPlayer.getCurrentTime();
    }

    return this.video ? this.video.currentTime : 0;
  }

  play() {
    if (this.isYouTube) {
      this.ytPlayer?.playVideo?.();
      return;
    }

    this.video?.play?.().catch(() => {});
  }

  pause() {
    if (this.isYouTube) {
      this.ytPlayer?.pauseVideo?.();
      return;
    }

    this.video?.pause?.();
  }

  setMuted(muted) {
    if (this.isYouTube) {
      if (muted) {
        this.ytPlayer?.mute?.();
      } else {
        this.ytPlayer?.unMute?.();
      }
      return;
    }

    if (this.video) {
      this.video.muted = muted;
    }
  }

  seekTo(seconds) {
    if (this.isYouTube) {
      this.ytPlayer?.seekTo?.(seconds, true);
      return;
    }

    if (this.video) {
      this.video.currentTime = seconds;
    }
  }

  onPlayPauseClick(event) {
    event.stopPropagation();

    if (this.isPlaying()) {
      this.pause();
    } else {
      this.play();
    }
  }

  onMuteClick(event) {
    event.stopPropagation();
    this.setMuted(!this.isMuted());
    this.updateMuteState();
  }

  onVideoClick(event) {
    if (event.target.closest('.fwb-video-player__controls')) return;

    if (this.isPlaying()) {
      this.pause();
    } else {
      this.play();
    }
  }

  onProgressInput(event) {
    event.stopPropagation();

    const duration = this.getDuration();
    if (!Number.isFinite(duration) || duration <= 0) return;

    const value = Number(event.target.value);
    const seekTime = (value / 100) * duration;
    this.seekTo(seekTime);
    this.updateProgressUI(value);
    this.currentTimeEl.textContent = this.formatTime(seekTime);
  }

  onTimeUpdate() {
    if (this.isSeeking || this.isYouTube) return;

    const duration = this.getDuration();
    if (!Number.isFinite(duration) || duration <= 0) return;

    const currentTime = this.getCurrentTime();
    const percent = (currentTime / duration) * 100;
    this.updateProgressUI(percent);
    this.currentTimeEl.textContent = this.formatTime(currentTime);
  }

  updateYouTubeProgress() {
    if (this.isSeeking || !this.isYouTube) return;

    const duration = this.getDuration();
    if (!Number.isFinite(duration) || duration <= 0) {
      this.durationEl.textContent = '0:00';
      return;
    }

    const currentTime = this.getCurrentTime();
    const percent = (currentTime / duration) * 100;
    this.durationEl.textContent = this.formatTime(duration);
    this.updateProgressUI(percent);
    this.currentTimeEl.textContent = this.formatTime(currentTime);
  }

  onLoadedMetadata() {
    const duration = this.getDuration();

    if (!Number.isFinite(duration) || duration <= 0) {
      this.durationEl.textContent = '0:00';
      return;
    }

    this.durationEl.textContent = this.formatTime(duration);
    this.onTimeUpdate();
  }

  updateProgressUI(percent) {
    if (!this.progress) return;

    const safePercent = Math.max(0, Math.min(100, percent));
    this.progress.value = String(safePercent);
    this.progress.setAttribute('aria-valuenow', String(Math.round(safePercent)));
  }

  updatePlayPauseState() {
    const playing = this.isPlaying();
    const playIcon = this.querySelector('.fwb-video-player__icon--play');
    const pauseIcon = this.querySelector('.fwb-video-player__icon--pause');

    if (playIcon) playIcon.hidden = playing;
    if (pauseIcon) pauseIcon.hidden = !playing;
    this.playPauseBtn?.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
  }

  updateMuteState() {
    const muted = this.isMuted();
    const soundOnIcon = this.querySelector('.fwb-video-player__icon--sound-on');
    const soundOffIcon = this.querySelector('.fwb-video-player__icon--sound-off');

    if (soundOnIcon) soundOnIcon.hidden = muted;
    if (soundOffIcon) soundOffIcon.hidden = !muted;
    this.muteBtn?.setAttribute('aria-label', muted ? 'Unmute video' : 'Mute video');
  }

  onPlay() {
    this.updatePlayPauseState();
  }

  onPause() {
    this.updatePlayPauseState();
  }
}

customElements.define('full-width-banner-video', FullWidthBannerVideo);
